import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  UserCredential,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { useToast } from './ToastContext';
import { useFavoritesStore } from '../stores/favoritesStore';

interface UserData {
  email: string;
  username: string;
  photoURL?: string;
  listening_time: number;
  stations_explored: number;
  top_genre: string;
  favorites: string[];
  recently_played: string[];
  total_favorites: number;
  last_played: Timestamp | null;
  most_played_station: string | null;
  createdAt: Timestamp;
  updated_at?: Timestamp;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<UserCredential>;
  logout: () => Promise<void>;
  userData: UserData | null;
  updateUserData: (data: Partial<UserData>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<UserData | null>(null);
  const { showToast } = useToast();
  const setUserId = useFavoritesStore(state => state.setUserId);

  const createUserDocument = async (user: User, additionalData?: Partial<UserData>) => {
    if (!user) return;

    const userRef = doc(db, 'users', user.uid);
    const snapshot = await getDoc(userRef);

    if (!snapshot.exists()) {
      const { email, photoURL, displayName } = user;

      try {
        await setDoc(userRef, {
          email,
          username: displayName || email?.split('@')[0] || 'User',
          photoURL: photoURL || '',
          createdAt: serverTimestamp(),
          listening_time: 0,
          stations_explored: 0,
          top_genre: '',
          favorites: [],
          recently_played: [],
          total_favorites: 0,
          last_played: null,
          most_played_station: null,
          ...additionalData
        });
      } catch (error) {
        console.error('Error creating user document:', error);
      }
    } else {
      // Always update profile picture and display name from Google
      if (user.photoURL) {
        try {
          await updateDoc(userRef, {
            photoURL: user.photoURL,
            username: user.displayName || snapshot.data()?.username
          });
        } catch (error) {
          console.error('Error updating user profile picture:', error);
        }
      }
    }

    // Get and set user data
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      setUserData(userDoc.data() as UserData);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      setUserId(user?.uid || null);

      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setUserData(userDoc.data() as UserData);
        } else {
          await createUserDocument(user);
        }
      } else {
        setUserData(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      showToast('Successfully signed in!', 'success');
    } catch (error: any) {
      console.error('Sign in error:', error);
      showToast(error.message || 'Failed to sign in', 'error');
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      await createUserDocument(user);
      showToast('Account created successfully!', 'success');
    } catch (error: any) {
      console.error('Sign up error:', error);
      showToast(error.message || 'Failed to create account', 'error');
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    try {
      const result = await signInWithPopup(auth, provider);
      await createUserDocument(result.user);
      showToast('Successfully signed in with Google!', 'success');
      return result;
    } catch (error: any) {
      console.error('Google sign in error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        showToast('Sign-in cancelled', 'info');
      } else if (error.code === 'auth/popup-blocked') {
        showToast('Please allow popups for this site', 'error');
      } else {
        showToast(error.message || 'Failed to sign in with Google', 'error');
      }
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUserId(null);
      showToast('Successfully signed out!', 'success');
    } catch (error: any) {
      console.error('Sign out error:', error);
      showToast(error.message || 'Failed to sign out', 'error');
      throw error;
    }
  };

  const updateUserData = async (data: Partial<UserData>) => {
    if (!user) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        ...data,
        updated_at: serverTimestamp()
      });

      // Update local state
      setUserData(prev => prev ? { ...prev, ...data } : null);
      showToast('Profile updated successfully!', 'success');
    } catch (error: any) {
      console.error('Update user data error:', error);
      showToast(error.message || 'Failed to update profile', 'error');
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn,
        signUp,
        signInWithGoogle,
        logout,
        userData,
        updateUserData
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 